import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Box, Button, CircularProgress, LinearProgress, Tab, Tabs, Typography } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import { Assessment, HeadsetMic, TrackChanges, AssignmentTurnedIn, TrendingUp, Phone } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import ForbiddenPage from "../../components/ForbiddenPage";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

import SalesDashboard from "./SalesDashboard";
import AttendanceDashboard from "./AttendanceDashboard";
import TrackingDashboard from "./TrackingDashboard";
import TasksDashboard from "./TasksDashboard";
import MetaAdsDashboard from "./MetaAdsDashboard";
import CallsDashboard from "./CallsDashboard";

const tabs = [
  { id: "sales", label: "Vendas", icon: Assessment },
  { id: "attendance", label: "Atendimento", icon: HeadsetMic },
  { id: "tracking", label: "Tracking", icon: TrackChanges },
  { id: "tasks", label: "Tarefas", icon: AssignmentTurnedIn },
  { id: "metaAds", label: "Meta Ads", icon: TrendingUp },
  { id: "calls", label: "Ligações", icon: Phone },
];

const localDate = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const reportDate = value => {
  if (!value) return "—";
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("pt-BR");
};

const formatReportTimestamp = (value, timeZone) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short", timeZone }).format(new Date(value));
  } catch (error) {
    return new Date(value).toLocaleString("pt-BR");
  }
};

const reportNumber = value => Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 });

const reportProgressLabel = phase => ({
  reading: "Lendo mensagens",
  analyzing: "Analisando lotes",
  consolidating: "Consolidando o relatório",
}[phase] || "Processando relatório");

const reportProgressPercent = progress => {
  if (!progress) return 0;
  const processed = Number(progress.processedBatches || 0);
  const total = Number(progress.totalBatches || 0);
  if (total > 0) return Math.max(0, Math.min(100, Math.round(processed / total * 100)));
  const processedMessages = Number(progress.processedMessages || 0);
  const totalMessages = Number(progress.totalMessages || 0);
  return totalMessages > 0 ? Math.max(0, Math.min(100, Math.round(processedMessages / totalMessages * 100))) : 0;
};

function AIReportPanel({ dateFrom, dateTo, selectedUserId, companyId, userId }) {
  const [report, setReport] = useState(null);
  const [reportFilters, setReportFilters] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [jobStatus, setJobStatus] = useState("");
  const [progress, setProgress] = useState(null);
  const storageKey = companyId && userId ? `aiReportJob:${companyId}:${userId}` : null;
  const timerRef = useRef(null);
  const requestRef = useRef(null);
  const runIdRef = useRef(0);
  const mountedRef = useRef(true);
  const filtersChanged = reportFilters && (
    reportFilters.dateFrom !== dateFrom || reportFilters.dateTo !== dateTo || reportFilters.selectedUserId !== selectedUserId
  );

  const clearStoredJob = useCallback(jobId => {
    if (!storageKey) return;
    try {
      const stored = JSON.parse(sessionStorage.getItem(storageKey) || "null");
      if (!jobId || stored?.jobId === jobId) sessionStorage.removeItem(storageKey);
    } catch (error) {
      try { sessionStorage.removeItem(storageKey); } catch (storageError) { /* Armazenamento de sessão indisponível. */ }
    }
  }, [storageKey]);

  const stopTracking = useCallback((jobId, message) => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    clearStoredJob(jobId);
    setGenerating(false);
    setJobStatus("");
    setProgress(null);
    setErrorMessage(message);
  }, [clearStoredJob]);

  const pollJob = useCallback(async (job, runId, delay = 5000, retryDelay = 5000) => {
    if (!mountedRef.current || runIdRef.current !== runId) return;
    if (Date.now() - job.startedAt >= 24 * 60 * 60 * 1000) {
      stopTracking(job.jobId, "O prazo de acompanhamento deste relatório expirou após 24 horas. Inicie uma nova geração para continuar.");
      return;
    }

    timerRef.current = setTimeout(async () => {
      if (!mountedRef.current || runIdRef.current !== runId) return;
      const source = axios.CancelToken.source();
      requestRef.current = source;
      try {
        const response = await api.get(`/dashboard/ai-reports/${encodeURIComponent(job.jobId)}`, { cancelToken: source.token });
        if (!mountedRef.current || runIdRef.current !== runId) return;
        const result = response.data || {};
        if (result.status === "completed" && result.report) {
          clearStoredJob(job.jobId);
          setReport(result.report);
          setReportFilters(job.filters);
          setGenerating(false);
          setJobStatus("");
          setProgress(null);
          setErrorMessage("");
          return;
        }
        if (result.status === "failed") {
          const message = typeof result.error === "string" && result.error.trim()
            ? `${result.error} Você pode clicar em Gerar relatório com os mesmos filtros para reutilizar os lotes concluídos.`
            : "A geração do relatório falhou. Clique em Gerar relatório com os mesmos filtros para reutilizar os lotes concluídos.";
          stopTracking(job.jobId, message);
          return;
        }
        if (result.status !== "processing") {
          stopTracking(job.jobId, "Não foi possível recuperar o relatório. O acompanhamento foi interrompido; você pode iniciar uma nova geração quando quiser.");
          return;
        }
        setProgress(result.progress || null);
        setJobStatus("processing");
        pollJob(job, runId, 5000, 5000);
      } catch (error) {
        if (axios.isCancel(error) || !mountedRef.current || runIdRef.current !== runId) return;
        const status = error?.response?.status;
        const serverMessage = error?.response?.data?.error;
        if (status === 404 || status === 403) {
          stopTracking(job.jobId, status === 404
            ? "Este relatório não está mais disponível. Inicie uma nova geração para continuar."
            : "Você não tem mais acesso a este relatório.");
          return;
        }
        if (!status || status >= 500) {
          const nextRetryDelay = Math.min(retryDelay * 2, 60000);
          setJobStatus("processing");
          setErrorMessage("");
          pollJob(job, runId, retryDelay, nextRetryDelay);
          return;
        }
        stopTracking(job.jobId, typeof serverMessage === "string" && serverMessage.trim()
          ? `Não foi possível acompanhar o relatório: ${serverMessage}`
          : "Não foi possível acompanhar este relatório. Inicie uma nova geração para continuar.");
      } finally {
        if (requestRef.current === source) requestRef.current = null;
      }
    }, delay);
  }, [stopTracking]);

  useEffect(() => {
    mountedRef.current = true;
    if (storageKey) {
      try {
        const stored = JSON.parse(sessionStorage.getItem(storageKey) || "null");
        if (stored?.jobId && stored?.filters && Number.isFinite(stored.startedAt)) {
          const job = stored;
          setReportFilters(job.filters);
          if (Date.now() - job.startedAt >= 24 * 60 * 60 * 1000) {
            stopTracking(job.jobId, "O prazo de acompanhamento deste relatório expirou após 24 horas. Inicie uma nova geração para continuar.");
          } else {
            setGenerating(true);
            setJobStatus("processing");
            setProgress(null);
            setErrorMessage("");
            runIdRef.current += 1;
            pollJob(job, runIdRef.current);
          }
        }
      } catch (error) {
        try { sessionStorage.removeItem(storageKey); } catch (storageError) { /* Armazenamento de sessão indisponível. */ }
      }
    }
    return () => {
      mountedRef.current = false;
      runIdRef.current += 1;
      clearTimeout(timerRef.current);
      timerRef.current = null;
      if (requestRef.current) requestRef.current.cancel("Relatório pausado ao sair da tela");
      requestRef.current = null;
    };
  }, [storageKey, pollJob]);

  const generate = async () => {
    if (generating) return;
    setGenerating(true);
    setErrorMessage("");
    setJobStatus("");
    setProgress(null);
    setReport(null);
    const snapshot = { dateFrom, dateTo, selectedUserId };
    let acceptedJob = false;
    try {
      const response = await api.post("/dashboard/ai-reports", {
        dateFrom: snapshot.dateFrom,
        dateTo: snapshot.dateTo,
        ...(snapshot.selectedUserId ? { selectedUserId: snapshot.selectedUserId } : {}),
      });
      const result = response.data || {};
      if ((response.status === 202 || result.jobId) && result.jobId) {
        acceptedJob = true;
        const job = { jobId: result.jobId, status: "processing", startedAt: Date.now(), filters: snapshot };
        setReportFilters(snapshot);
        setJobStatus("processing");
        setProgress(result.progress || null);
        if (storageKey) {
          try { sessionStorage.setItem(storageKey, JSON.stringify(job)); } catch (error) { /* A sessão pode estar sem espaço. */ }
        }
        runIdRef.current += 1;
        if (mountedRef.current) pollJob(job, runIdRef.current);
        return;
      }
      const completedReport = result.status === "completed" && result.report ? result.report : result;
      if (!mountedRef.current) return;
      setReport(completedReport);
      setReportFilters(snapshot);
    } catch (error) {
      const serverMessage = error?.response?.data?.error;
      if (mountedRef.current) setErrorMessage(typeof serverMessage === "string" && serverMessage.trim()
        ? serverMessage : "Não foi possível gerar o relatório. Tente novamente.");
    } finally {
      if (mountedRef.current && !acceptedJob) setGenerating(false);
    }
  };

  return <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", minWidth: 0, gap: 14 }}>
    <section style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, padding: "18px 20px", background: "#103b30", color: "#fff", borderRadius: 16 }}>
      <div style={{ maxWidth: 620 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#bce9d5", fontSize: 11, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase" }}><Assessment style={{ fontSize: 15 }} /> Leitura do período</div>
        <h2 style={{ margin: "8px 0 4px", fontSize: 21, letterSpacing: "-.03em" }}>Relatórios com IA</h2>
        <p style={{ margin: 0, color: "#d1e5dc", fontSize: 13 }}>Gere um retrato das métricas e dos redirecionamentos para as datas selecionadas.</p>
      </div>
      <Button variant="contained" onClick={generate} disabled={generating || !dateFrom || !dateTo}
        startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <Assessment />}
        sx={{ background: "#b9f2d3", color: "#103b30", fontWeight: 700, textTransform: "none", "&:hover": { background: "#a2e9c4" }, "&.Mui-disabled": { color: "#75968a" } }}>
        {generating ? "Gerando relatório…" : "Gerar relatório"}
      </Button>
    </section>

    {jobStatus === "processing" && <div role="status" aria-live="polite" style={{ padding: 14, borderRadius: 10, border: "1px solid #bfdfcc", background: "#f0f8f3", color: "#265b3f", fontSize: 13 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <strong>{reportProgressLabel(progress?.phase)}</strong>
        {progress && progress.phase !== "reading" && <span style={{ fontVariantNumeric: "tabular-nums" }}>{reportProgressPercent(progress)}%</span>}
      </div>
      {progress && <>
        {progress.phase === "reading"
          ? <LinearProgress aria-label="Leitura das mensagens em andamento" sx={{ my: 1.25, borderRadius: 99, backgroundColor: "#d8eade", "& .MuiLinearProgress-bar": { backgroundColor: "#34835c" } }} />
          : <div role="progressbar" aria-label="Progresso do relatório" aria-valuemin={0} aria-valuemax={100} aria-valuenow={reportProgressPercent(progress)} style={{ height: 6, margin: "9px 0", overflow: "hidden", borderRadius: 99, background: "#d8eade" }}>
            <div style={{ width: `${reportProgressPercent(progress)}%`, height: "100%", borderRadius: 99, background: "#34835c", transition: "width .3s ease" }} />
          </div>}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", color: "#426a53", fontSize: 12 }}>
          {progress.phase === "reading"
            ? <span>Mensagens lidas: {reportNumber(progress.processedMessages)}</span>
            : <>
              <span>Lotes: {reportNumber(progress.processedBatches)} de {reportNumber(progress.totalBatches)}</span>
              <span>Mensagens: {reportNumber(progress.processedMessages)} de {reportNumber(progress.totalMessages)}</span>
            </>}
          <span>Tokens: {reportNumber(progress.inputTokens)} de entrada · {reportNumber(progress.outputTokens)} de saída</span>
        </div>
      </>}
      {!progress && <div style={{ marginTop: 5 }}>Preparando os dados do relatório. O acompanhamento será retomado se você navegar para outra tela nesta sessão.</div>}
    </div>}
    {errorMessage && <div role="alert" style={{ padding: 13, borderRadius: 10, border: "1px solid #f0b8b2", background: "#fff2f0", color: "#9d241d", fontSize: 13 }}>{errorMessage}</div>}
    {!report && !generating && !errorMessage && <div style={{ padding: "24px 20px", border: "1px dashed #cbd8d1", borderRadius: 14, background: "#fff", color: "#61736a", fontSize: 13 }}>
      O relatório será criado quando você clicar em <strong>Gerar relatório</strong>. Alterar os filtros não inicia uma geração.
    </div>}

    {report && <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", minWidth: 0, gap: 12 }}>
      <section style={{ padding: "13px 16px", border: "1px solid #dce6e0", borderRadius: 12, background: "#fff", display: "flex", flexWrap: "wrap", gap: "8px 22px", color: "#53655c", fontSize: 12 }}>
        <span><strong style={{ color: "#22372e" }}>Gerado em</strong> {formatReportTimestamp(report.generatedAt, report.period?.timeZone)} {report.period?.timeZone ? `(${report.period.timeZone})` : ""}</span>
        <span><strong style={{ color: "#22372e" }}>Período do resultado</strong> {reportDate(report.period?.dateFrom)} a {reportDate(report.period?.dateTo)}</span>
      </section>
      {filtersChanged && <div role="status" style={{ padding: 12, borderRadius: 10, background: "#fff7df", border: "1px solid #f0dfa8", color: "#715512", fontSize: 12 }}>
        Os filtros mudaram. Este resultado continua referente ao período exibido acima; gere um novo relatório para consultar os filtros atuais.
      </div>}

      <section style={{ padding: "16px 18px", border: "1px solid #dce6e0", borderRadius: 14, background: "#fff" }}>
        <h3 style={{ margin: "0 0 9px", color: "#183b2f", fontSize: 15 }}>Resumo</h3>
        <p style={{ margin: 0, color: "#42564c", fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{report.summary || "Sem resumo disponível."}</p>
      </section>

      <section style={{ padding: "16px 18px", border: "1px solid #dce6e0", borderRadius: 14, background: "#fff" }}>
        <h3 style={{ margin: "0 0 12px", color: "#183b2f", fontSize: 15 }}>Indicadores por atendente</h3>
        <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760, fontSize: 12 }}>
          <thead><tr>{["Atendente", "Contatos", "Atendimentos", "Em atendimento agora", "Tempo no período", "Tempo médio concluídos", "Concluídos"].map(label => <th key={label} scope="col" style={{ padding: "9px 8px", borderBottom: "1px solid #dfe7e2", color: "#738078", textAlign: label === "Atendente" ? "left" : "right", fontWeight: 700 }}>{label}</th>)}</tr></thead>
          <tbody>{(report.metrics || []).map((metric, index) => <tr key={metric.userId || `${metric.userName}-${index}`}>
            <th scope="row" style={{ padding: "10px 8px", borderBottom: "1px solid #edf1ee", textAlign: "left", color: "#263a31", fontWeight: 650 }}>{metric.userName || "Atendente não identificado"}</th>
            {[metric.contacts, metric.tickets, metric.openTickets, `${reportNumber(Number(metric.totalAttendanceSeconds || 0) / 60)} min`, metric.averageAttendanceSeconds === null ? "Sem registro" : `${reportNumber(Number(metric.averageAttendanceSeconds) / 60)} min`, metric.completedTickets].map((value, cell) => <td key={cell} style={{ padding: "10px 8px", borderBottom: "1px solid #edf1ee", textAlign: "right", color: "#42564c", fontVariantNumeric: "tabular-nums" }}>{value}</td>)}
          </tr>)}
          {!report.metrics?.length && <tr><td colSpan={7} style={{ padding: 16, color: "#738078", textAlign: "center" }}>Nenhuma métrica disponível para este período.</td></tr>}</tbody>
        </table></div>
      </section>

      <section style={{ padding: "16px 18px", border: "1px solid #dce6e0", borderRadius: 14, background: "#fff" }}>
        <h3 style={{ margin: "0 0 4px", color: "#183b2f", fontSize: 15 }}>Redirecionamentos</h3>
        <p style={{ margin: "0 0 12px", color: "#758078", fontSize: 12 }}>Registros encontrados no período, com a atribuição informada para cada ocorrência.</p>
        {(report.redirects || []).length ? <div style={{ display: "grid", gap: 10 }}>
          {report.redirects.map((redirect, index) => <article key={`${redirect.phone}-${index}`} style={{ padding: 13, border: "1px solid #e4ebe6", borderRadius: 11, background: "#fbfcfb" }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <strong style={{ color: "#1e392e", fontSize: 13 }}>{redirect.phone || "Telefone não informado"}</strong>
              <span style={{ color: "#65766d", fontSize: 11 }}>{reportNumber(redirect.contactCount)} contatos · {reportNumber(redirect.occurrenceCount ?? (redirect.occurrences || []).length)} ocorrências</span>
            </div>
            {Number(redirect.occurrenceCount) > (redirect.occurrences || []).length && <p style={{ margin: "0 0 10px", color: "#718078", fontSize: 11 }}>Exibindo {(redirect.occurrences || []).length} de {reportNumber(redirect.occurrenceCount)} evidências.</p>}
            {(redirect.users || []).map((sender, senderIndex) => <p key={sender.userId || senderIndex} style={{ color: "#52685c", fontSize: 12 }}>
              {sender.userName}: {reportNumber(sender.contactCount)} contatos receberam esse direcionamento
              {sender.attribution === "registro" ? " (autor registrado)" : sender.attribution === "responsável do atendimento" ? " (responsável pelo atendimento)" : " (origem não identificada)"}.
            </p>)}
            <div style={{ display: "grid", gap: 9 }}>{(redirect.occurrences || []).map((item, occurrenceIndex) => {
              const allowedAttribution = ["registro", "responsável do atendimento", "não identificado"];
              const attribution = allowedAttribution.includes(String(item.attribution || "").toLocaleLowerCase("pt-BR"))
                ? String(item.attribution).toLocaleLowerCase("pt-BR") : "não identificado";
              return <div key={item.messageId || `${item.ticketId}-${occurrenceIndex}`} style={{ paddingLeft: 11, borderLeft: "2px solid #9bcbb1" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", color: "#718078", fontSize: 11 }}>
                  <span>{formatReportTimestamp(item.createdAt, report.period?.timeZone)}</span>
                  <span>Origem: {attribution}</span>
                  {item.userName && attribution === "registro" && <span>Atendente registrado: {item.userName}</span>}
                  {item.userName && attribution === "responsável do atendimento" && <span>Responsável do atendimento: {item.userName}</span>}
                </div>
                <p style={{ margin: "5px 0 0", color: "#354a3f", fontSize: 12, lineHeight: 1.55, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{item.body || "Sem texto registrado."}</p>
              </div>;
            })}</div>
          </article>)}
        </div> : <div style={{ padding: 13, borderRadius: 10, background: "#f6f8f6", color: "#718078", fontSize: 12 }}>Nenhum redirecionamento encontrado no período.</div>}
      </section>

      {!!report.warnings?.length && <section aria-label="Observações do relatório" style={{ padding: "13px 16px", border: "1px solid #f0dfa8", borderRadius: 12, background: "#fff9e9", color: "#715512", fontSize: 12 }}>
        <strong>Observações</strong><ul style={{ margin: "6px 0 0", paddingLeft: 19 }}>{report.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
      </section>}
    </div>}
  </div>;
}

const useStyles = makeStyles(() => ({
  root: { height: "100%", overflowY: "auto", background: "#f7f9fc", color: "#14211b" },
  nav: { position: "sticky", top: 0, zIndex: 5, display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", borderBottom: "1px solid #e1e6e4", padding: "0 14px" },
  content: { width: "calc(100% - 28px)", margin: "0 auto", padding: "18px 0 40px" },
  heading: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 },
  filters: { display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", "& input, & select": { height: 34, padding: "0 9px", border: "1px solid #dce4df", borderRadius: 9, background: "#fff" } },
  "@media(max-width:600px)": { nav: { padding: "0 4px", overflowX: "auto" }, content: { width: "calc(100% - 18px)", paddingTop: 10 } },
  "@media print": { nav: { display: "none" }, root: { overflow: "visible", height: "auto" }, content: { width: "100%", margin: 0, padding: 0 } },
}));

export default function Dashboard() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const isManager = user.profile === "manager";
  const initialAIView = new URLSearchParams(location.search).get("view") === "ai-reports";
  const [active, setActive] = useState(isManager || initialAIView ? "attendance" : "sales");
  const [attendanceView, setAttendanceView] = useState(isManager || initialAIView ? "reports" : "indicadores");
  const [dateFrom, setDateFrom] = useState(() => localDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [dateTo, setDateTo] = useState(() => localDate(new Date()));
  const [selectedUserId, setSelectedUserId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!isManager);
  const sourceRef = useRef(null);
  const canViewAIReports = user.profile === "admin" || user.profile === "manager";

  const load = useCallback(async () => {
    if (sourceRef.current) sourceRef.current.cancel();
    const source = axios.CancelToken.source();
    sourceRef.current = source;
    setLoading(true);
    try {
      const response = await api.get("/dashboard/v2/overview", {
        params: { dateFrom, dateTo, selectedUserId: selectedUserId || undefined },
        cancelToken: source.token,
      });
      setData(response.data);
    } catch (error) {
      if (!axios.isCancel(error)) toastError(error);
    } finally {
      if (sourceRef.current === source) setLoading(false);
    }
  }, [dateFrom, dateTo, selectedUserId]);

  useEffect(() => {
    if (user.profile === "manager") {
      setLoading(false);
      return undefined;
    }
    load();
    return () => { if (sourceRef.current) sourceRef.current.cancel(); };
  }, [load, user.profile]);

  useEffect(() => {
    if (user.profile === "manager") {
      setActive("attendance");
      setAttendanceView("reports");
    } else if (new URLSearchParams(location.search).get("view") === "ai-reports" && user.profile === "admin") {
      setActive("attendance");
      setAttendanceView("reports");
    }
  }, [location.search, user.profile]);

  if (!isManager && user.showDashboard !== "enabled") return <ForbiddenPage />;

  const panels = {
    sales: <SalesDashboard data={data?.sales} />,
    attendance: canViewAIReports && attendanceView === "reports"
      ? <AIReportPanel dateFrom={dateFrom} dateTo={dateTo} selectedUserId={user.profile === "admin" ? selectedUserId : ""} companyId={user.companyId} userId={user.id} />
      : <AttendanceDashboard data={data?.attendance} />,
    tracking: <TrackingDashboard data={data?.tracking} />,
    tasks: <TasksDashboard data={data?.tasks} users={data?.options?.users || []} isAdmin={user.profile === "admin"} onChanged={load} />,
    metaAds: <MetaAdsDashboard data={data?.metaAds} isAdmin={user.profile === "admin"} dateFrom={dateFrom} dateTo={dateTo} onChanged={load} />,
    calls: <CallsDashboard data={data?.calls} />,
  };

  if (isManager && !canViewAIReports) return <ForbiddenPage />;

  return <MainContainer><div className={classes.root}>
    <div className={classes.nav}>
      {isManager ? <Typography sx={{ px: 2, py: 1.5, fontSize: 13, fontWeight: 700, color: "#18513d" }}>Relatórios com IA</Typography> : <>
        <Tabs value={active} onChange={(_, value) => setActive(value)} variant="scrollable" scrollButtons="auto"
          TabIndicatorProps={{ style: { background: "#078453" } }}>
          {tabs.map(item => <Tab key={item.id} value={item.id} icon={<item.icon style={{ fontSize: 15 }} />}
            label={item.label} sx={{ minHeight: 46, minWidth: 90, textTransform: "none", fontSize: 11, flexDirection: "row", gap: 0.7 }} />)}
        </Tabs>
        <Button size="small" onClick={() => window.print()} sx={{ whiteSpace: "nowrap", color: "#52615a" }}>Exportar PDF</Button>
      </>}
    </div>
    <div className={classes.content}>
      <div className={classes.heading}>
        <Typography component="h1" sx={{ fontSize: { xs: 23, md: 29 }, fontWeight: 800, letterSpacing: "-.04em" }}>
          Dashboard de {tabs.find(item => item.id === active)?.label.toLowerCase()}.
        </Typography>
        <div className={classes.filters}>
          <input type="date" aria-label="Data inicial" value={dateFrom} max={dateTo} onChange={event => setDateFrom(event.target.value)} />
          <input type="date" aria-label="Data final" value={dateTo} min={dateFrom} onChange={event => setDateTo(event.target.value)} />
          {!isManager && user.profile === "admin" && !["tracking", "metaAds"].includes(active) &&
            <select aria-label="Usuário" value={selectedUserId} onChange={event => setSelectedUserId(event.target.value)}>
              <option value="">Todos os usuários</option>
              {(data?.options?.users || []).map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
            </select>}
          {!isManager && <Button size="small" variant="outlined" onClick={load}>Atualizar</Button>}
        </div>
      </div>
      {active === "attendance" && canViewAIReports && !isManager && <Tabs value={attendanceView} onChange={(_, value) => setAttendanceView(value)} aria-label="Seção de atendimento"
        sx={{ minHeight: 38, mb: 1.5, borderBottom: "1px solid #e1e6e4", "& .MuiTab-root": { minHeight: 38, textTransform: "none", fontSize: 12, fontWeight: 650 } }}>
        <Tab value="indicadores" label="Indicadores" />
        <Tab value="reports" label="Relatórios com IA" />
      </Tabs>}
      {loading && !data ? <Box height={320} display="grid" sx={{ placeItems: "center" }}><CircularProgress /></Box> : panels[active]}
    </div>
  </div></MainContainer>;
}
